import React, { useEffect, useState, useCallback } from "react";
import {
  Menu,
  ArrowRight,
  X,
  BarChart,
  FileText,
  ShieldCheck,
  Settings,
  Zap,
  Target,
  Cloud,
  Check,
  ChevronDown,
  Home,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Share2,
  Camera,
  Sparkles,
  Instagram,
  Facebook,
  Linkedin,
} from "lucide-react";
import AuthScreen from "./AuthScreen";
import Dashboard from "./Dashboard";
import Logo from "./Logo";
import SampleSummaryModal from "./SampleSummaryModal";
import DetailedUnderwritingReport from "./DetailedUnderwritingReport";
import ReferralProgramPage from "./ReferralProgramPage";
import PricingCalculator from "./PricingCalculator";
import ParticleCanvas from "./ParticleCanvas";
import {
  fetchUserSubscription,
  setGlobalAuthErrorHandler,
  clearGlobalAuthErrorHandler,
} from "../api";
import {
  getTokens as readTokens,
  setTokens as persistTokens,
} from "../../shared/utils/secureTokenStorage";
import { useBranding } from "../../shared/contexts/BrandingContext";

// Local virtual staging images
import livingroomBefore from '../../VirtualStaging/livingroom-before.jpg';
import livingroomAfter from '../../VirtualStaging/livingroom-after.jpg';

const LandingHeader = ({
  isScrolled,
  scrollToSection,
  onLoginClick,
  onReferralClick,
  onBlogClick,
  onSelectUseCase,
  isUseCaseOpen,
  setIsUseCaseOpen,
  isMobileMenuOpen,
  toggleMobileMenu,
}) => (
  <header
    className={`fixed w-full z-50 transition-all duration-300 ${isScrolled
      ? "bg-slate-950/95 backdrop-blur-md shadow-lg border-b border-slate-800 py-3"
      : "bg-transparent py-6"
      }`}
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center">
        <div className="cursor-pointer" onClick={() => scrollToSection("top")}>
          <Logo />
        </div>

        <div className="hidden md:flex items-center gap-8">
          <nav className="flex gap-6">
            <div
              className="relative"
              onMouseEnter={() => setIsUseCaseOpen(true)}
              onMouseLeave={() => setIsUseCaseOpen(false)}
            >
              <button
                className="text-slate-300 hover:text-white font-medium transition-colors inline-flex items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsUseCaseOpen((prev) => !prev);
                }}
              >
                Use Cases <ChevronDown className="w-4 h-4" />
              </button>
              {isUseCaseOpen && (
                <div
                  className="absolute left-0 top-full w-56 bg-slate-900 border border-slate-800 rounded-lg shadow-xl"
                  onMouseEnter={() => setIsUseCaseOpen(true)}
                  onMouseLeave={() => setIsUseCaseOpen(false)}
                >
                  <button
                    onClick={() => {
                      onSelectUseCase("lender");
                      setIsUseCaseOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-slate-200 hover:bg-slate-800"
                  >
                    Lender
                  </button>
                  <button
                    onClick={() => {
                      onSelectUseCase("borrower");
                      setIsUseCaseOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-slate-200 hover:bg-slate-800"
                  >
                    Borrower
                  </button>
                  <button
                    onClick={() => {
                      window.location.href = "/flow";
                    }}
                    className="block w-full text-left px-4 py-2 text-slate-200 hover:bg-slate-800"
                  >
                    Real Estate Professionals
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => scrollToSection("products")}
              className="text-slate-300 hover:text-white font-medium transition-colors"
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection("pricing")}
              className="text-slate-300 hover:text-white font-medium transition-colors"
            >
              Pricing
            </button>
            {/* Referral Program - Coming Soon
            <button
              onClick={onReferralClick}
              className="text-slate-300 hover:text-white font-medium transition-colors"
            >
              Referral Program
            </button>
            */}
            <button
              onClick={onBlogClick}
              className="text-slate-300 hover:text-white font-medium transition-colors"
            >
              Blog
            </button>
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={onLoginClick}
              className="px-4 py-2 text-slate-300 hover:text-white font-medium transition-colors"
            >
              Login
            </button>
            <button
              onClick={onLoginClick}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>

        <button className="md:hidden text-white p-2" onClick={toggleMobileMenu}>
          <Menu className="w-8 h-8" />
        </button>
      </div>
    </div>

    {isMobileMenuOpen && (
      <div className="absolute top-full left-0 w-full bg-slate-900 shadow-xl md:hidden border-t border-slate-800">
        <div className="flex flex-col p-4 space-y-4">
          <div className="border border-slate-800 rounded">
            <div className="text-slate-400 uppercase text-xs px-4 pt-3 pb-1 tracking-wide">
              Use Cases
            </div>
            <button
              onClick={() => {
                onSelectUseCase("lender");
                toggleMobileMenu();
              }}
              className="text-slate-200 text-left py-2 px-4 hover:bg-slate-800 w-full"
            >
              Lender
            </button>
            <button
              onClick={() => {
                onSelectUseCase("borrower");
                toggleMobileMenu();
              }}
              className="text-slate-200 text-left py-2 px-4 hover:bg-slate-800 w-full"
            >
              Borrower
            </button>
            <button
              onClick={() => {
                window.location.href = "/flow";
              }}
              className="text-slate-200 text-left py-2 px-4 hover:bg-slate-800 w-full"
            >
              Real Estate Professionals
            </button>
          </div>
          <button
            onClick={() => {
              scrollToSection("products");
              toggleMobileMenu();
            }}
            className="text-slate-200 text-left py-2 px-4 hover:bg-slate-800 rounded"
          >
            Features
          </button>
          <button
            onClick={() => scrollToSection("pricing")}
            className="text-slate-200 text-left py-2 px-4 hover:bg-slate-800 rounded"
          >
            Pricing
          </button>
          <button
            onClick={() => {
              onBlogClick();
              toggleMobileMenu();
            }}
            className="text-slate-200 text-left py-2 px-4 hover:bg-slate-800 rounded"
          >
            Blog
          </button>
          <button
            onClick={onLoginClick}
            className="text-white text-left py-2 px-4 border border-slate-600 rounded font-semibold hover:bg-white hover:text-slate-900"
          >
            Login / Sign Up
          </button>
        </div>
      </div>
    )}
  </header>
);

const HeroSection = ({
  scrollToSection,
  onSummaryClick,
  onDetailedReportClick,
  onVirtualStagingClick,
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const messages = [
    {
      text: "A coordinated team of AI agents that manages leads, reviews documents, detects gaps, calculates risk, and produces underwriting-ready reports—so your team can close loans faster.",
      badge: "For Mortgage Teams",
      buttonText: "Sample Report",
      buttonAction: onDetailedReportClick,
      buttonColor: "bg-blue-600 hover:bg-blue-700",
    },
    {
      text: "Powerful AI tools designed specifically for realtors and real estate professionals to market listings and close deals faster.",
      badge: "For Real Estate Professionals",
      buttonText: "Learn More",
      buttonAction: () => window.location.href = '/flow',
      buttonColor: "bg-emerald-600 hover:bg-emerald-700",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
        setIsAnimating(false);
      }, 500);
    }, 5000);
    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <section
      id="top"
      className="relative pt-40 pb-24 lg:pt-52 lg:pb-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-slate-900 to-slate-950 z-0"></div>
      <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] z-0"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6 leading-tight animate-slide-up">
          Your A.I Team,
          <br className="hidden md:block" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-400">
            {" "}
            On Demand.
          </span>
        </h1>

        {/* Rotating Message Section */}
        <div
          className="mb-10 max-w-3xl mx-auto animate-slide-up delay-100 opacity-0"
          style={{ animationFillMode: "forwards" }}
        >
          {/* Badge */}
          <div className="h-8 mb-3 flex justify-center items-center">
            {messages[currentMessageIndex].badge && (
              <span
                className={`inline-block text-emerald-400 text-sm font-semibold tracking-wider uppercase px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 transition-all duration-500 ${
                  isAnimating ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
                }`}
              >
                {messages[currentMessageIndex].badge}
              </span>
            )}
          </div>

          {/* Message Text */}
          <p
            className={`text-xl md:text-2xl text-slate-300 leading-relaxed transition-all duration-500 ${
              isAnimating ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
            }`}
          >
            {messages[currentMessageIndex].text}
          </p>

          {/* Slide Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {messages.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAnimating(true);
                  setTimeout(() => {
                    setCurrentMessageIndex(index);
                    setIsAnimating(false);
                  }, 300);
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentMessageIndex
                    ? "bg-sky-400 w-8"
                    : "bg-slate-600 hover:bg-slate-500"
                }`}
                aria-label={`Go to message ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Dynamic Button based on current message */}
        <div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up delay-200 opacity-0"
          style={{ animationFillMode: "forwards" }}
        >
          <button
            onClick={messages[currentMessageIndex].buttonAction}
            className={`px-8 py-4 text-white rounded-xl font-bold text-lg transition-all duration-300 ${messages[currentMessageIndex].buttonColor} ${
              isAnimating ? "opacity-0 scale-95" : "opacity-100 scale-100"
            }`}
          >
            {messages[currentMessageIndex].buttonText}
          </button>
        </div>
      </div>
    </section>
  );
};

const ProblemSection = ({ activeTeam, setActiveTeam }) => {
  const mortgageProblems = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Manual Document Review",
      desc: "Underwriters spend hours manually reviewing and reconciling documents.",
    },
    {
      icon: <BarChart className="w-6 h-6" />,
      title: "Inconsistent Calculations",
      desc: "Manual calculations lead to errors and inconsistencies.",
    },
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: "Compliance Risks",
      desc: "Manual processes increase the risk of compliance violations.",
    },
  ];

  const realtyProblems = [
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Empty Listings Don't Sell",
      desc: "Vacant properties sit on the market longer and sell for less than staged homes.",
    },
    {
      icon: <Share2 className="w-6 h-6" />,
      title: "Social Media is Time-Consuming",
      desc: "Creating engaging content for multiple platforms takes hours every week.",
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Standing Out is Hard",
      desc: "In a crowded market, getting noticed by potential buyers requires constant effort.",
    },
  ];

  const problems = activeTeam === "mortgage" ? mortgageProblems : realtyProblems;
  const headline = activeTeam === "mortgage"
    ? "The mortgage process is slow, complex, and frustrating."
    : "Selling properties is competitive and time-consuming.";

  return (
    <section className="py-24 bg-slate-950 relative">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
            {headline}
          </h2>
          <p className="text-lg text-slate-400">We're here to change that.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {problems.map((problem, idx) => (
            <div
              key={idx}
              className={`group p-8 bg-slate-900 rounded-2xl border border-slate-800 hover:bg-slate-900/80 transition-all ${
                activeTeam === "mortgage"
                  ? "hover:border-sky-500/50"
                  : "hover:border-emerald-500/50"
              }`}
            >
              <div className={`w-12 h-12 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 ${
                activeTeam === "mortgage" ? "text-sky-400" : "text-emerald-400"
              }`}>
                {problem.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                {problem.title}
              </h3>
              <p className="text-slate-400">{problem.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Interactive Before/After Slider for Virtual Staging Preview
const VirtualStagingPreview = () => {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [activeStyle, setActiveStyle] = useState(0);
  const containerRef = React.useRef(null);

  const styles = [
    { name: "Modern", color: "from-slate-600 to-slate-800" },
    { name: "Scandinavian", color: "from-amber-200 to-amber-400" },
    { name: "Luxury", color: "from-yellow-400 to-amber-500" },
    { name: "Coastal", color: "from-cyan-400 to-blue-500" },
  ];

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.min(Math.max((x / rect.width) * 100, 5), 95);
    setSliderPos(pct);
  };

  const handleMouseMove = (e) => isDragging && handleMove(e.clientX);
  const handleTouchMove = (e) => isDragging && handleMove(e.touches[0].clientX);

  React.useEffect(() => {
    const handleUp = () => setIsDragging(false);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchend", handleUp);
    };
  }, []);

  return (
    <div className="bg-slate-900 rounded-xl p-4 h-full">
      {/* Before/After Comparison Slider */}
      <div
        ref={containerRef}
        className="relative aspect-[4/3] rounded-lg overflow-hidden cursor-ew-resize select-none mb-4"
        onMouseDown={() => setIsDragging(true)}
        onMouseMove={handleMouseMove}
        onTouchStart={() => setIsDragging(true)}
        onTouchMove={handleTouchMove}
      >
        {/* After Image (Background) */}
        <img
          src={livingroomAfter}
          alt="After staging"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />

        {/* Before Image (Clipped) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
        >
          <img
            src={livingroomBefore}
            alt="Before staging"
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        </div>

        {/* Slider Line */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10"
          style={{ left: `${sliderPos}%` }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-xl flex items-center justify-center">
            <div className="flex items-center gap-0.5">
              <ChevronLeft className="w-3 h-3 text-slate-600" />
              <ChevronRight className="w-3 h-3 text-slate-600" />
            </div>
          </div>
        </div>

        {/* Labels */}
        <span className="absolute top-2 left-2 px-2 py-1 bg-black/70 backdrop-blur-sm text-white text-xs rounded-full font-medium">
          Before
        </span>
        <span className="absolute top-2 right-2 px-2 py-1 bg-violet-500/90 backdrop-blur-sm text-white text-xs rounded-full font-medium">
          After
        </span>
      </div>

      {/* Style Selector */}
      <div className="flex gap-1.5 mb-4">
        {styles.map((style, i) => (
          <button
            key={style.name}
            onClick={() => setActiveStyle(i)}
            className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
              i === activeStyle
                ? "bg-violet-500 text-white shadow-lg shadow-violet-500/30"
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            {style.name}
          </button>
        ))}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-slate-800/70 rounded-lg p-2 text-center">
          <p className="text-violet-400 font-bold text-sm">30s</p>
          <p className="text-slate-500 text-[10px]">Generate</p>
        </div>
        <div className="bg-slate-800/70 rounded-lg p-2 text-center">
          <p className="text-violet-400 font-bold text-sm">8</p>
          <p className="text-slate-500 text-[10px]">Styles</p>
        </div>
        <div className="bg-slate-800/70 rounded-lg p-2 text-center">
          <p className="text-violet-400 font-bold text-sm">4K</p>
          <p className="text-slate-500 text-[10px]">Resolution</p>
        </div>
        <div className="bg-slate-800/70 rounded-lg p-2 text-center">
          <p className="text-violet-400 font-bold text-sm">95%</p>
          <p className="text-slate-500 text-[10px]">Savings</p>
        </div>
      </div>

      {/* Flow Branding */}
      <div className="mt-4 flex items-center justify-center gap-2 text-slate-500 text-xs">
        <Sparkles className="w-3 h-3 text-violet-400" />
        <span>Powered by <span className="text-violet-400 font-semibold">Flow</span></span>
      </div>
    </div>
  );
};

const RealtorFeaturesSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const slides = [
    {
      id: "social-media",
      badge: "Social Media Marketing",
      badgeIcon: <Share2 className="w-4 h-4" />,
      title: "AI-Powered Social Media Content",
      subtitle: "For Real Estate Professionals",
      description:
        "Generate engaging property listings, market updates, and branded content for all your social platforms. Our AI creates scroll-stopping posts that drive engagement and attract qualified leads.",
      features: [
        "Auto-generate property showcase posts",
        "Create market insight infographics",
        "Schedule content across platforms",
        "Track engagement analytics",
      ],
      visualContent: (
        <div className="bg-slate-900 rounded-xl p-6 h-full">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                <Instagram className="w-5 h-5 text-white" />
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Facebook className="w-5 h-5 text-white" />
              </div>
              <div className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center">
                <Linkedin className="w-5 h-5 text-white" />
              </div>
            </div>
            <span className="text-slate-400 text-sm">Connected Platforms</span>
          </div>
          <div className="space-y-3">
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-sky-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium mb-1">
                    Just Listed in Beverly Hills
                  </p>
                  <p className="text-slate-400 text-xs">
                    Stunning 4BR/3BA with panoramic views...
                  </p>
                  <div className="flex gap-4 mt-2 text-xs text-slate-500">
                    <span>2.4K views</span>
                    <span>186 likes</span>
                    <span>24 shares</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                  <BarChart className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium mb-1">
                    Q1 Market Report
                  </p>
                  <p className="text-slate-400 text-xs">
                    Local market trends and insights...
                  </p>
                  <div className="flex gap-4 mt-2 text-xs text-slate-500">
                    <span>1.8K views</span>
                    <span>142 likes</span>
                    <span>56 shares</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      gradient: "from-pink-500/20 via-purple-500/20 to-blue-500/20",
    },
    {
      id: "virtual-staging",
      badge: "Flow Virtual Staging",
      badgeIcon: <Camera className="w-4 h-4" />,
      title: "Stage Any Space in Seconds",
      subtitle: "AI-Powered Virtual Staging",
      description:
        "Transform empty properties into stunning, fully-furnished spaces with AI. No furniture, no photographers, no waiting. Get photorealistic staging at a fraction of the cost.",
      features: [
        "8 designer-curated styles",
        "Up to 4K resolution output",
        "30-second generation time",
        "Save 95% vs traditional staging",
      ],
      visualContent: <VirtualStagingPreview />,
      gradient: "from-violet-500/20 via-purple-500/20 to-pink-500/20",
    },
  ];

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const currentSlideData = slides[currentSlide];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative overflow-hidden">
      {/* Background gradient based on current slide */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${currentSlideData.gradient} opacity-30 transition-all duration-700`}
      ></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-emerald-400 text-sm font-semibold tracking-wider uppercase mb-4 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            Realtor Tools
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Marketing &amp; Staging
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-sky-400">
              Made Easy
            </span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Automate your social media presence and transform empty spaces into
            beautifully staged homes with AI.
          </p>
        </div>

        {/* Slider Container */}
        <div className="relative">
          {/* Slide Content */}
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            {/* Left: Text Content */}
            <div className="flex-1 order-2 lg:order-1">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 ${
                currentSlideData.id === "virtual-staging"
                  ? "bg-violet-500/10 border border-violet-500/20"
                  : "bg-sky-500/10 border border-sky-500/20"
              }`}>
                <span className={currentSlideData.id === "virtual-staging" ? "text-violet-400" : "text-sky-400"}>
                  {currentSlideData.badgeIcon}
                </span>
                <span className={`text-sm font-medium ${
                  currentSlideData.id === "virtual-staging" ? "text-violet-400" : "text-sky-400"
                }`}>
                  {currentSlideData.badge}
                </span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold mb-2 text-white">
                {currentSlideData.title}
              </h3>
              <p className={`text-lg mb-6 ${
                currentSlideData.id === "virtual-staging" ? "text-violet-400" : "text-sky-400"
              }`}>
                {currentSlideData.subtitle}
              </p>
              <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                {currentSlideData.description}
              </p>
              <ul className="space-y-4 mb-8">
                {currentSlideData.features.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      currentSlideData.id === "virtual-staging" ? "bg-violet-500/20" : "bg-sky-500/20"
                    }`}>
                      <Check className={`w-3.5 h-3.5 ${
                        currentSlideData.id === "virtual-staging" ? "text-violet-400" : "text-sky-400"
                      }`} />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              {currentSlideData.id === "virtual-staging" ? (
                <a
                  href="/flow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-violet-500/25 transition-all"
                >
                  Explore Flow <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-sky-500 text-white rounded-xl font-semibold hover:bg-sky-600 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Right: Visual Content */}
            <div className="flex-1 order-1 lg:order-2">
              <div className="relative group">
                <div className="absolute -inset-4 bg-gradient-to-r from-sky-500/20 to-blue-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-slate-800/50 border border-slate-700 rounded-2xl p-2 overflow-hidden group-hover:border-sky-500/50 transition-all duration-300">
                  {currentSlideData.visualContent}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 w-12 h-12 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 hover:border-sky-500/50 transition-all z-20"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 w-12 h-12 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 hover:border-sky-500/50 transition-all z-20"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center gap-3 mt-12">
          {slides.map((slide, index) => {
            const isVirtual = slide.id === "virtual-staging";
            const activeColor = isVirtual ? "violet" : "sky";
            return (
              <button
                key={slide.id}
                onClick={() => goToSlide(index)}
                className={`group flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  index === currentSlide
                    ? isVirtual
                      ? "bg-violet-500/20 border border-violet-500/50"
                      : "bg-sky-500/20 border border-sky-500/50"
                    : "bg-slate-800/50 border border-slate-700 hover:border-slate-600"
                }`}
                aria-label={`Go to ${slide.badge}`}
              >
                <div
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide
                      ? isVirtual ? "bg-violet-400" : "bg-sky-400"
                      : "bg-slate-500"
                  }`}
                ></div>
                <span
                  className={`text-sm font-medium transition-all ${
                    index === currentSlide
                      ? isVirtual ? "text-violet-400" : "text-sky-400"
                      : "text-slate-500"
                  }`}
                >
                  {slide.badge}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const VirtualStagingModal = ({ isOpen, onClose }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(0);

  const rooms = [
    {
      name: "Living Room",
      beforeImage: livingroomBefore,
      afterImage: livingroomAfter,
    },
    {
      name: "Bedroom",
      beforeImage: "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=800&auto=format&fit=crop&q=80&sat=-100",
      afterImage: "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=800&auto=format&fit=crop&q=80",
    },
    {
      name: "Kitchen",
      beforeImage: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&auto=format&fit=crop&q=80&sat=-100",
      afterImage: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&auto=format&fit=crop&q=80",
    },
  ];

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = (x / rect.width) * 100;
    setSliderPosition(percentage);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    const x = Math.max(0, Math.min(touch.clientX - rect.left, rect.width));
    const percentage = (x / rect.width) * 100;
    setSliderPosition(percentage);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-slate-900 rounded-2xl border border-slate-700 max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800">
          <div>
            <h2 className="text-2xl font-bold text-white">AI Virtual Staging</h2>
            <p className="text-slate-400 text-sm mt-1">
              Drag the slider to see the before &amp; after transformation
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Room Selector */}
        <div className="flex gap-2 p-4 border-b border-slate-800 overflow-x-auto">
          {rooms.map((room, index) => (
            <button
              key={room.name}
              onClick={() => {
                setSelectedRoom(index);
                setSliderPosition(50);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                index === selectedRoom
                  ? "bg-emerald-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white"
              }`}
            >
              {room.name}
            </button>
          ))}
        </div>

        {/* Before/After Slider */}
        <div className="p-6">
          <div
            className="relative aspect-[16/10] rounded-xl overflow-hidden cursor-ew-resize select-none"
            onMouseMove={handleMouseMove}
            onMouseDown={() => setIsDragging(true)}
            onMouseUp={() => setIsDragging(false)}
            onMouseLeave={() => setIsDragging(false)}
            onTouchMove={handleTouchMove}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={() => setIsDragging(false)}
          >
            {/* After Image (Full) */}
            <img
              src={rooms[selectedRoom].afterImage}
              alt="After staging"
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />

            {/* Before Image (Clipped) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
            >
              <img
                src={rooms[selectedRoom].beforeImage}
                alt="Before staging"
                className="absolute inset-0 w-full h-full object-cover grayscale"
                draggable={false}
              />
            </div>

            {/* Slider Handle */}
            <div
              className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
              style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center">
                <div className="flex gap-0.5">
                  <ChevronLeft className="w-4 h-4 text-slate-600" />
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </div>
              </div>
            </div>

            {/* Labels */}
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-slate-900/90 rounded-lg text-white text-sm font-medium">
              Before
            </div>
            <div className="absolute top-4 right-4 px-3 py-1.5 bg-emerald-500/90 rounded-lg text-white text-sm font-medium">
              After
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700">
              <p className="text-2xl font-bold text-emerald-400">90%</p>
              <p className="text-slate-400 text-sm">Cost Savings</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700">
              <p className="text-2xl font-bold text-emerald-400">24hr</p>
              <p className="text-slate-400 text-sm">Turnaround</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700">
              <p className="text-2xl font-bold text-emerald-400">40%</p>
              <p className="text-slate-400 text-sm">Faster Sales</p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-6 text-center">
            <button className="px-8 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-all">
              Get Started with Virtual Staging
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CapabilitiesSection = ({ onLoginClick }) => (
  <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-5xl font-extrabold text-white mb-4">
          Powerful Features, Built for Lenders
        </h2>
        <p className="text-lg text-slate-400 max-w-3xl mx-auto">
          CloudMortgage.ai is designed to help you close more loans, faster,
          with less manual work.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[
          {
            icon: <Target className="w-8 h-8" />,
            title: "Income Calculations",
            desc: "W-2, 1099, K-1, Rental, DSCR",
          },
          {
            icon: <BarChart className="w-8 h-8" />,
            title: "Asset & Reserve Verification",
            desc: "Complete financial position analysis",
          },
          {
            icon: <FileText className="w-8 h-8" />,
            title: "Liability Reconciliation",
            desc: "All obligations accounted for",
          },
          {
            icon: <Settings className="w-8 h-8" />,
            title: "DTI & Housing Ratios",
            desc: "Automated calculations with audit trail",
          },
          {
            icon: <Home className="w-8 h-8" />,
            title: "Collateral Details",
            desc: "Complete property analysis",
          },
          {
            icon: <ShieldCheck className="w-8 h-8" />,
            title: "Risks & Conditions",
            desc: "Source-linked audit trail included",
          },
        ].map((feature, idx) => (
          <div
            key={idx}
            className="group p-8 bg-slate-800/50 rounded-2xl border border-slate-800 hover:border-sky-500/50 hover:bg-slate-800 transition-all duration-300"
          >
            <div className="w-16 h-16 bg-slate-900 text-sky-400 border border-slate-700 rounded-full flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
              {feature.icon}
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">
              {feature.title}
            </h3>
            <p className="text-slate-400">{feature.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ProductShowcaseSection = ({ onLoginClick, activeTeam }) => {
  // Skip rendering entirely for realty team since content is shown elsewhere
  if (activeTeam === "realty") return null;

  return (
    <section
      id="products"
      className="py-32 bg-slate-950 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-transparent opacity-50"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-24">
          <span className="inline-block text-sm font-semibold tracking-wider uppercase mb-4 px-4 py-2 rounded-full border text-sky-400 bg-sky-500/10 border-sky-500/20">
            Product Features
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Everything You Need to
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-400">
              Delight Your Customers
            </span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Built for modern mortgage professionals who believe technology should work harder—so they can focus on serving borrowers, not paperwork.
          </p>
        </div>

        {/* Mortgage Teams Content */}
        <>

      {/* Feature 1 - Case Management Dashboard */}
      <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 mb-32 group">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-l from-sky-500/20 to-blue-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative bg-slate-800/50 border border-slate-700 rounded-2xl p-2 overflow-hidden group-hover:scale-105 transform transition-transform duration-300">
              <div
                className="relative w-full"
                style={{ paddingBottom: "56.25%" }}
              >
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-xl"
                  src="https://www.youtube.com/embed/eAoAyiCN_Ss"
                  title="Case Management Dashboard Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 mb-6">
            <span className="text-sky-400 text-xl">📊</span>
            <span className="text-sky-400 text-sm font-medium">All-in-One</span>
          </div>
          <h3 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Case Management Dashboard
          </h3>
          <p className="text-slate-400 text-lg mb-8 leading-relaxed">
            Track every loan from application to closing. Kanban boards, task
            management, document storage, and team collaboration all in one
            place.
          </p>
          <ul className="space-y-4 mb-8">
            {[
              "Kanban pipeline view",
              "Document management",
              "Team collaboration",
              "Progress tracking",
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-slate-300">
                <div className="w-6 h-6 rounded-full bg-sky-500/20 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-sky-400" />
                </div>
                {item}
              </li>
            ))}
          </ul>
          <button
            onClick={onLoginClick}
            className="inline-flex items-center gap-2 text-sky-400 font-semibold hover:gap-3 transition-all"
          >
            Explore Dashboard <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Feature 2 - Borrower Portal */}
      <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 mb-32 group">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-l from-sky-500/20 to-blue-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative bg-slate-800/50 border border-slate-700 rounded-2xl p-2 overflow-hidden group-hover:scale-105 transform transition-transform duration-300">
              <div className="bg-slate-900 rounded-xl aspect-video flex items-center justify-center">
                <div className="p-8 w-full">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-white font-bold">
                      JD
                    </div>
                    <div>
                      <p className="text-white font-medium">John Doe</p>
                      <p className="text-slate-500 text-sm">Loan #2024-001</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">
                        Application Progress
                      </span>
                      <span className="text-sky-400">75%</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full w-3/4 bg-gradient-to-r from-sky-400 to-blue-500 rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-4">
                      {["Applied", "Docs", "Review", "Closing"].map(
                        (step, i) => (
                          <div
                            key={i}
                            className={`text-center text-xs ${i < 3 ? "text-sky-400" : "text-slate-500"}`}
                          >
                            {step}
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 mb-6">
            <span className="text-sky-400 text-xl">👥</span>
            <span className="text-sky-400 text-sm font-medium">
              Client Experience
            </span>
          </div>
          <h3 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Borrower Portal
          </h3>
          <p className="text-slate-400 text-lg mb-8 leading-relaxed">
            Give your clients a premium experience. They can upload documents,
            track progress, and communicate with you—all in one place.
          </p>
          <ul className="space-y-4 mb-8">
            {[
              "Secure document upload",
              "Real-time status updates",
              "Integrated messaging",
              "Mobile-friendly",
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-slate-300">
                <div className="w-6 h-6 rounded-full bg-sky-500/20 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-sky-400" />
                </div>
                {item}
              </li>
            ))}
          </ul>
          <button
            onClick={onLoginClick}
            className="inline-flex items-center gap-2 text-sky-400 font-semibold hover:gap-3 transition-all"
          >
            See Borrower Portal <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Feature 3 - AI Underwriting Report */}
      <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 mb-32 group">
        <div className="flex-1 order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 mb-6">
            <span className="text-sky-400 text-xl">🤖</span>
            <span className="text-sky-400 text-sm font-medium">AI-Powered</span>
          </div>
          <h3 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            A.I Underwriting Report
          </h3>
          <p className="text-slate-400 text-lg mb-8 leading-relaxed">
            Get comprehensive loan analysis in minutes, not hours. Our AI
            reviews documents, calculates DTI, verifies income, and flags
            potential issues automatically.
          </p>
          <ul className="space-y-4 mb-8">
            {[
              "Automated document analysis",
              "Income & asset verification",
              "Risk assessment & flags",
              "Compliance checking",
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-slate-300">
                <div className="w-6 h-6 rounded-full bg-sky-500/20 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-sky-400" />
                </div>
                {item}
              </li>
            ))}
          </ul>
          <button
            onClick={onLoginClick}
            className="inline-flex items-center gap-2 text-sky-400 font-semibold hover:gap-3 transition-all"
          >
            Try A.I Reports <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 order-1 lg:order-2">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-sky-500/20 to-blue-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative bg-slate-800/50 border border-slate-700 rounded-2xl p-6 overflow-hidden group-hover:scale-105 transform transition-transform duration-300">
              {/* 5-Step Process Visualizer */}
              <div className="bg-slate-900 rounded-xl p-6">
                <h4 className="text-center text-white text-xl font-bold mb-8">
                  How It Works
                </h4>

                {/* Steps Row */}
                <div className="flex items-center justify-between relative">
                  {/* Connecting Line */}
                  <div className="absolute top-5 left-[10%] right-[10%] h-0.5 bg-slate-700"></div>

                  {[
                    "Create a case",
                    "Invite borrower",
                    "AI underwriting",
                    "Human review",
                    "Approval",
                  ].map((title, idx) => (
                    <div
                      key={idx}
                      className="relative z-10 flex flex-col items-center text-center group flex-1"
                    >
                      <div className="w-10 h-10 rounded-full bg-slate-800 border-2 border-sky-500/50 flex items-center justify-center text-sky-400 font-bold text-sm mb-2">
                        {idx + 1}
                      </div>
                      <p className="text-white text-xs font-medium">{title}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Video Demo */}
              <div className="mt-4">
                <div
                  className="relative w-full"
                  style={{ paddingBottom: "56.25%" }}
                >
                  <iframe
                    className="absolute top-0 left-0 w-full h-full rounded-xl border border-slate-700"
                    src="https://www.youtube.com/embed/YjdwT31HxaA"
                    title="CloudMortgage.io Demo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <p className="text-center text-slate-500 text-xs mt-3">
                  Watch AI Underwriting in action
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature 3 - AI Meeting Assistant */}
      <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 mb-32 group">
        <div className="flex-1 order-2 lg:order-1">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 mb-6">
            <span className="text-sky-400 text-xl">🎙️</span>
            <span className="text-sky-400 text-sm font-medium">
              Smart Automation
            </span>
          </div>
          <h3 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            A.I Meeting Assistant
          </h3>
          <p className="text-slate-400 text-lg mb-8 leading-relaxed">
            Never miss a detail. You can send CloudMortgage.io Meeting Assistant
            to your calls, takes notes, while you focus on closing the deals.
          </p>
          <ul className="space-y-4 mb-8">
            {[
              "Real-time transcription",
              "Action item extraction",
              "Auto follow-up drafts",
              "Meeting summaries",
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-slate-300">
                <div className="w-6 h-6 rounded-full bg-sky-500/20 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-sky-400" />
                </div>
                {item}
              </li>
            ))}
          </ul>
          <button
            onClick={onLoginClick}
            className="inline-flex items-center gap-2 text-sky-400 font-semibold hover:gap-3 transition-all"
          >
            Meet Your Meeting Assistant <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 order-1 lg:order-2">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-sky-500/20 to-blue-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative bg-slate-800/50 border border-slate-700 rounded-2xl p-2 overflow-hidden group-hover:scale-105 transform transition-transform duration-300">
              <div className="bg-slate-900 rounded-xl aspect-video flex items-center justify-center">
                <div className="text-center p-8 w-full">
                  <div className="flex items-center justify-center gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className="w-1.5 bg-sky-400 rounded-full animate-pulse"
                        style={{
                          height: `${20 + Math.random() * 30}px`,
                          animationDelay: `${i * 0.1}s`,
                        }}
                      ></div>
                    ))}
                  </div>
                  <p className="text-slate-400 text-sm">Note taking...</p>
                  <div className="mt-4 bg-slate-800 rounded-lg p-3 text-left">
                    <p className="text-slate-500 text-xs mb-2">
                      Live Transcript
                    </p>
                    <p className="text-slate-300 text-sm">
                      "Let's discuss the loan terms for the Smith property..."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-32">
            {[
              { icon: "⚡", value: "12-30", label: "Hours Saved Per File" },
              { icon: "🎯", value: "3x", label: "Faster Processing" },
              { icon: "📈", value: "98%", label: "Accuracy Rate" },
              { icon: "⏱️", value: "50%", label: "Fewer Conditions" },
            ].map((stat, i) => (
              <div
                key={i}
                className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 text-center hover:border-sky-500/50 hover:bg-slate-800/80 transition-all duration-300"
              >
                <div className="text-3xl mb-3">{stat.icon}</div>
                <div className="font-bold text-3xl text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-400 mb-1">
                  {stat.value}
                </div>
                <div className="text-slate-500 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </>
        )}
      </div>
    </section>
  );
};

const HowItWorksSection = () => (
  <section id="how-it-works" className="py-24 bg-slate-950">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-4xl font-bold text-center text-white mb-16">
        How It Works
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 relative">
        <div className="hidden lg:block absolute top-8 left-[6%] right-[6%] h-1 bg-slate-800 -z-0"></div>
        {[
          {
            step: "1",
            title: "Create a case",
            desc: "Lender sets up the case with property details and uploads initial documents.",
          },
          {
            step: "2",
            title: "Invite the borrower",
            desc: "Borrower gets a welcome email with the case number to access the portal and share documents.",
          },
          {
            step: "3",
            title: "AI underwriting",
            desc: "Once documents are ready, generate an AI underwriting report and resolve flagged issues with the borrower.",
          },
          {
            step: "4",
            title: "Human review",
            desc: "After issues are addressed, send the full file to the underwriting team for approval.",
          },
          {
            step: "5",
            title: "Approval",
            desc: "With a clean, organized file, the underwriting team can approve faster.",
          },

          // { step: <Zap className="w-6 h-6" />, title: "Express Pass", desc: "Priority processing for urgent closings.", badge: "Optional", isExpress: true }
        ].map((item, idx) => (
          <div
            key={idx}
            className="relative z-10 flex flex-col items-center text-center group"
          >
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg border-4 border-slate-950 transition-transform duration-300 group-hover:scale-110 ${item.isExpress
                ? "bg-amber-500 text-amber-950"
                : "bg-slate-800 text-sky-400 border-sky-900"
                }`}
            >
              {item.step}
            </div>
            <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              {item.desc}
            </p>
            {item.badge && (
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${item.isExpress
                  ? "bg-amber-500/20 text-amber-400"
                  : "bg-sky-500/20 text-sky-400"
                  }`}
              >
                {item.badge}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Video Demo */}
      <div className="mt-16 max-w-4xl mx-auto">
        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          <iframe
            className="absolute top-0 left-0 w-full h-full rounded-2xl shadow-2xl border border-slate-800"
            src="https://www.youtube.com/embed/YjdwT31HxaA"
            title="CloudMortgage.io Demo"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
        <p className="text-center text-slate-400 text-sm mt-4">
          Watch how CloudMortgage.io streamlines the lending process
        </p>
      </div>
    </div>
  </section>
);

const FAQSection = ({ activeTeam }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const mortgageFaqs = [
    {
      question: "What is CloudMortgage.io?",
      answer:
        "A modern, AI-powered lending platform that helps banks, credit unions, brokers, and borrowers move from application to clear-to-close faster with fewer back-and-forths.",
    },
    {
      question: "How does it help close loans faster?",
      answer:
        "The platform centralizes borrower docs, automates underwriting steps, and keeps everyone in sync with real-time status updates—cutting days off typical timelines.",
    },
    {
      question: "Why should lenders trust CloudMortgage.io?",
      answer:
        "Enterprise-grade security, encrypted transfers, and scoped permissions for shared Drive folders. Secrets are never exposed in the frontend, and documents are stored securely.",
    },
    {
      question: "Do borrowers need special tools?",
      answer:
        "No. Borrowers signed up for a free account, login and enter the lender-shared case number, and upload or share a folder—no extra software required.",
    },
    {
      question: "How does the AI underwriting pipeline work?",
      answer:
        "Lenders request an AI report from a case; the system ingests borrower documents, runs eligibility and risk checks, and generates a structured underwriting summary. The report is delivered back into the case as a ready-to-review PDF so lenders can approve, condition, or counter faster.",
    },
    {
      question: "What does an AI underwriting report cost?",
      answer:
        "Standard reports cost 200 credits and Deep Reasoning reports cost 1,000 credits. Free tier includes a 9-hour SLA; Paid plans include a faster turnaround.",
    },
    {
      question: "How do lenders and borrowers get started?",
      answer:
        "Lenders sign up, create a new case, and share the case number. Borrowers use that case number,login and share the folder for document intake.",
    },
  ];

  const realtyFaqs = [
    {
      question: "What is AI Virtual Staging?",
      answer:
        "AI Virtual Staging uses artificial intelligence to digitally furnish and decorate empty rooms in property photos, creating photorealistic images that help buyers visualize the space.",
    },
    {
      question: "How long does virtual staging take?",
      answer:
        "Our AI can stage a room in minutes. Most orders are completed within 24 hours, compared to days or weeks for traditional staging.",
    },
    {
      question: "How much does virtual staging cost compared to traditional staging?",
      answer:
        "Virtual staging costs up to 90% less than traditional staging. No furniture rental, no movers, no storage fees—just beautiful, staged photos.",
    },
    {
      question: "What social media platforms do you support?",
      answer:
        "We support all major platforms including Instagram, Facebook, LinkedIn, Twitter/X, and TikTok. Create once, publish everywhere.",
    },
    {
      question: "Can I customize the furniture style?",
      answer:
        "Yes! Choose from multiple design styles including Modern, Traditional, Minimalist, Luxury, Farmhouse, and more to match your listing's target market.",
    },
    {
      question: "How does AI social media content creation work?",
      answer:
        "Simply input your property details and our AI generates engaging captions, hashtags, and post ideas optimized for each platform's audience and algorithm.",
    },
    {
      question: "Do I need design experience to use these tools?",
      answer:
        "Not at all! Our tools are designed for real estate professionals of all skill levels. The AI handles the complex work while you focus on selling.",
    },
  ];

  const faqs = activeTeam === "mortgage" ? mortgageFaqs : realtyFaqs;
  const accentColor = activeTeam === "mortgage" ? "sky" : "emerald";

  return (
    <section className="py-24 bg-slate-950">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center text-white mb-14">
          FAQs
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {faqs.map((faq, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className={`rounded-2xl border border-slate-800 bg-slate-900/70 transition-all shadow-lg shadow-black/20 group ${
                  activeTeam === "mortgage"
                    ? "hover:border-sky-500/60"
                    : "hover:border-emerald-500/60"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : idx)}
                  className="w-full flex items-center justify-between gap-3 px-6 py-5 text-left"
                >
                  <span className={`text-lg font-bold text-white ${
                    activeTeam === "mortgage"
                      ? "group-hover:text-sky-400"
                      : "group-hover:text-emerald-400"
                  }`}>
                    {faq.question}
                  </span>
                  <ArrowRight
                    className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${
                      isOpen
                        ? `rotate-90 ${activeTeam === "mortgage" ? "text-sky-400" : "text-emerald-400"}`
                        : activeTeam === "mortgage"
                          ? "group-hover:text-sky-400"
                          : "group-hover:text-emerald-400"
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-6 pb-5">
                    <p className="text-slate-400 leading-relaxed text-sm">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const OutcomesSection = () => (
  <section
    id="outcomes"
    className="py-24 bg-gradient-to-b from-slate-900 to-slate-950"
  >
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-5xl font-extrabold text-center text-white mb-12">
        Key Outcomes
      </h2>
      <div className="overflow-hidden rounded-xl border border-slate-700 shadow-lg bg-slate-800/50">
        <table className="w-full text-left border-collapse">
          <thead className="border-b border-slate-700">
            <tr>
              <th className="p-5 font-semibold text-lg text-slate-200 w-1/2">
                Outcome
              </th>
              <th className="p-5 font-semibold text-lg text-slate-200 w-1/2">
                Value
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {[
              {
                outcome: "Underwriter saves 12–30 hours per loan file",
                value: "More loans closed with the same staff",
              },
              {
                outcome: "Consistent underwriting calculations",
                value: "Reduced QC findings & repurchase risk",
              },
              {
                outcome: "Faster Clear-to-Underwrite",
                value: "Higher pull-through + better realtor relationships",
              },
              {
                outcome: "Human-underwriter-in-the-loop",
                value: "Fully compliant; no decision automation",
              },
              {
                outcome: "Scalable during volume spikes",
                value: "No emergency hiring or overtime burnout",
              },
            ].map((row, idx) => (
              <tr key={idx} className="hover:bg-slate-700/50 transition-colors">
                <td className="p-5 text-slate-300 font-medium">
                  {row.outcome}
                </td>
                <td className="p-5 text-slate-400">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

const WhyChooseUsSection = () => (
  <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-5xl font-extrabold text-center text-white mb-16">
        Why Lenders Choose Us
      </h2>
      <div className="grid md:grid-cols-3 gap-10">
        {[
          {
            icon: "🎯",
            title: "Handles Difficult Borrowers",
            desc: "Self-employed, multiple LLCs, multiple properties, DSCR, portfolio investors.",
          },
          {
            icon: "🔍",
            title: "Transparent & Explainable",
            desc: "Every number is source-linked — ready for audit or secondary market review.",
          },
          {
            icon: "⚙️",
            title: "Full options and add-ons",
            desc: "Users can select from a range of options and add-ons to customize their experience including comprehensive reports, accelerated delivery, and more",
          },
        ].map((item, idx) => (
          <div
            key={idx}
            className="text-center p-8 bg-slate-800/50 rounded-2xl border border-slate-800 hover:border-sky-500/50 hover:bg-slate-800 transition-all"
          >
            <div className="w-24 h-24 mx-auto bg-slate-900 rounded-full flex items-center justify-center text-5xl mb-6 shadow-lg text-white border border-slate-700">
              {item.icon}
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
            <p className="text-slate-400 leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PricingSection = ({ onLoginClick, activeTeam }) => {
  const mortgagePlans = [
    {
      name: "Founder Edition",
      price: "$0",
      features: [
        { active: true, text: "Unlimited Cases" },
        { active: true, text: "Unlimited Documents Uploaded" },
        { active: true, text: "Borrower Portal" },
        { active: true, text: "Case Management Dashboard" },
        { active: true, text: "Executive Dashboard (P&L and Tasks)" },
        { active: true, text: "Task Management" },
        { active: true, text: "P&L Tracker" },
        { active: true, text: "Notes Management and Collaboration" },
        { active: true, text: "Team Chat: Join only" },
        { active: true, text: "5000 A.I credits included" },
        { active: true, text: "A.I Report (9-hour processing time)" },
        { active: true, text: "AI Assistant-Underwriting Intelligence Assistant" },
        { active: true, text: "AI Assistant-Meeting Assistant" },
      ],
    },
    {
      name: "Starter",
      price: "$39.99",
      featured: true,
      features: [
        { active: true, text: "Unlimited Cases" },
        { active: true, text: "Unlimited Documents Uploaded" },
        { active: true, text: "Borrower Portal" },
        { active: true, text: "Case Management Dashboard" },
        { active: true, text: "Executive Dashboard (P&L and Tasks)" },
        { active: true, text: "Task Management" },
        { active: true, text: "P&L Tracker" },
        { active: true, text: "Notes Management and Collaboration" },
        { active: true, text: "Team Chat: Admin" },
        { active: true, text: "Team: 3 users" },
        { active: true, text: "10,000 A.I credits per month" },
        { active: true, text: "A.I Report (6-hour processing time)" },
        { active: true, text: "AI Assistant-Underwriting Intelligence Assistant" },
        { active: true, text: "AI Assistant-Meeting Assistant" },
      ],
    },
    {
      name: "Pro",
      price: "$79.99",
      featured: false,
      features: [
        { active: true, text: "Unlimited Cases" },
        { active: true, text: "Unlimited Documents Uploaded" },
        { active: true, text: "Borrower Portal" },
        { active: true, text: "Case Management Dashboard" },
        { active: true, text: "Executive Dashboard (P&L and Tasks)" },
        { active: true, text: "Task Management" },
        { active: true, text: "P&L Tracker" },
        { active: true, text: "Notes Management and Collaboration" },
        { active: true, text: "Team Chat: Admin" },
        { active: true, text: "Team: 10 users" },
        { active: true, text: "20,000 A.I credits per month" },
        { active: true, text: "A.I Report (3-hour processing time)" },
        { active: true, text: "AI Assistant-Underwriting Intelligence Assistant" },
        { active: true, text: "AI Assistant-Meeting Assistant" },
      ],
    },
  ];

  const realtyPlans = [
    {
      name: "Starter",
      price: "$29",
      features: [
        { active: true, text: "10 Virtual Staging images/month" },
        { active: true, text: "5 Social Media posts/month" },
        { active: true, text: "3 Design styles" },
        { active: true, text: "24-hour turnaround" },
        { active: true, text: "Basic analytics" },
        { active: false, text: "Priority support" },
        { active: false, text: "Custom branding" },
      ],
    },
    {
      name: "Professional",
      price: "$79",
      featured: true,
      features: [
        { active: true, text: "50 Virtual Staging images/month" },
        { active: true, text: "Unlimited Social Media posts" },
        { active: true, text: "All design styles" },
        { active: true, text: "Same-day turnaround" },
        { active: true, text: "Advanced analytics" },
        { active: true, text: "Priority support" },
        { active: false, text: "Custom branding" },
      ],
    },
    {
      name: "Agency",
      price: "$199",
      featured: false,
      features: [
        { active: true, text: "Unlimited Virtual Staging" },
        { active: true, text: "Unlimited Social Media posts" },
        { active: true, text: "All design styles" },
        { active: true, text: "Instant turnaround" },
        { active: true, text: "Advanced analytics" },
        { active: true, text: "Priority support" },
        { active: true, text: "Custom branding" },
      ],
    },
  ];

  const plans = activeTeam === "mortgage" ? mortgagePlans : realtyPlans;
  const accentColor = activeTeam === "mortgage" ? "sky" : "emerald";
  const subtitle = activeTeam === "mortgage"
    ? "Choose the plan that fits your volume"
    : "Choose the plan that fits your listings";

  return (
    <section id="pricing" className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-extrabold text-white mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-slate-400">{subtitle}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`bg-slate-900 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-slate-800 relative overflow-hidden group hover:border-slate-700 ${
                plan.featured
                  ? `border-2 ${activeTeam === "mortgage" ? "border-sky-600 hover:shadow-sky-900/20" : "border-emerald-600 hover:shadow-emerald-900/20"} transform md:-translate-y-4 shadow-2xl`
                  : ""
              }`}
            >
              {plan.featured && (
                <div className={`absolute top-0 left-1/2 -translate-x-1/2 ${activeTeam === "mortgage" ? "bg-sky-600" : "bg-emerald-600"} text-white px-6 py-1.5 rounded-b-xl font-bold text-sm shadow-md`}>
                  Most Popular
                </div>
              )}
              <div className="text-center border-b border-slate-800 pb-8 mb-8 mt-4">
                <h3 className="text-2xl font-bold text-white mb-4">
                  {plan.name}
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span
                    className={`text-5xl font-bold ${plan.featured ? (activeTeam === "mortgage" ? "text-sky-400" : "text-emerald-400") : "text-slate-200"}`}
                  >
                    {plan.price}
                  </span>
                  <span className="text-slate-500 font-medium">/month</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {plan.features.map((feat, i) => (
                  <li
                    key={i}
                    className={`flex items-center gap-3 ${feat.active ? "text-slate-300" : "text-slate-600 opacity-60"}`}
                  >
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                        feat.active
                          ? activeTeam === "mortgage"
                            ? "bg-sky-900/50 text-sky-400"
                            : "bg-emerald-900/50 text-emerald-400"
                          : "bg-slate-800 text-slate-600"
                      }`}
                    >
                      {feat.active ? (
                        <Check className="w-4 h-4" strokeWidth={3} />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                    </div>
                    <span className={feat.active ? "font-medium" : ""}>
                      {feat.text}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={onLoginClick}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 border ${
                  plan.featured
                    ? activeTeam === "mortgage"
                      ? "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-900/50"
                      : "bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-900/50"
                    : "bg-slate-800 text-white hover:bg-slate-700 border-slate-700"
                }`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <p className="text-slate-400 mb-6 text-lg">
            {activeTeam === "mortgage"
              ? "Need higher volume or custom processing times?"
              : "Need custom solutions for your brokerage?"}
          </p>
          <button className={`px-8 py-3 border rounded-xl font-bold text-lg transition-colors ${
            activeTeam === "mortgage"
              ? "border-sky-500/50 text-sky-400 hover:bg-sky-900/20"
              : "border-emerald-500/50 text-emerald-400 hover:bg-emerald-900/20"
          }`}>
            Contact Sales for Enterprise Pricing
          </button>
        </div>
      </div>
    </section>
  );
};
const PageFooter = ({ onReferralClick }) => (
  <footer className="bg-black text-slate-500 py-12 border-t border-slate-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm mb-4">
        {/* Referral Program - Coming Soon
        {onReferralClick ? (
          <button
            type="button"
            onClick={onReferralClick}
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Referral Program
          </button>
        ) : (
          <a href="/referral-program" className="text-slate-400 hover:text-slate-200 transition-colors">
            Referral Program
          </a>
        )}
        */}
        <a
          href="/terms"
          className="text-slate-400 hover:text-slate-200 transition-colors"
        >
          Terms
        </a>
        <a
          href="/privacy"
          className="text-slate-400 hover:text-slate-200 transition-colors"
        >
          Privacy
        </a>
        <a
          href="/credit-terms"
          className="text-slate-400 hover:text-slate-200 transition-colors"
        >
          Credit Terms
        </a>
      </div>
      <p>© 2026 CloudMortgage.io. All rights reserved.</p>
    </div>
  </footer>
);

const UseCasePage = ({
  title,
  subtitle,
  body,
  onBack,
  onSelectUseCase,
  onReferralClick,
}) => {
  const [localUseCaseOpen, setLocalUseCaseOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans">
      <LandingHeader
        isScrolled
        scrollToSection={() => onBack()}
        onLoginClick={onBack}
        onReferralClick={onReferralClick}
        onBlogClick={() => {
          window.location.href = "/blog";
        }}
        onSelectUseCase={onSelectUseCase}
        isUseCaseOpen={localUseCaseOpen}
        setIsUseCaseOpen={setLocalUseCaseOpen}
        isMobileMenuOpen={false}
        toggleMobileMenu={() => { }}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-10">
        <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-8 shadow-xl space-y-4">
          <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300 text-xs font-semibold uppercase tracking-widest">
            Use Case
          </p>
          <h1 className="text-4xl font-extrabold text-white">{title}</h1>
          <p className="text-lg text-slate-300">{subtitle}</p>
          <div className="text-slate-200 leading-relaxed whitespace-pre-line text-base">
            {body}
          </div>
          <div className="mt-6 border-t border-slate-800 pt-6">
            <h3 className="text-2xl font-bold text-white mb-4">
              Value & Pricing at a Glance
            </h3>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 space-y-3">
                <h4 className="text-xl font-semibold text-white">
                  🔥 Value Comparison
                </h4>
                <div className="p-4 bg-slate-800/50 rounded-2xl border border-slate-700 space-y-1">
                  <p className="text-white font-semibold">Human Underwriter</p>
                  <p className="text-sm text-slate-400">
                    Cost per Loan: $150–$840
                  </p>
                  <p className="text-sm text-slate-400">
                    Time to Result: 12–48 hours
                  </p>
                  <p className="text-sm text-slate-400">Scalability: Low</p>
                  <p className="text-sm text-slate-400">
                    Accuracy/Auditability: Medium (human error)
                  </p>
                </div>
                <div className="p-4 bg-slate-800/50 rounded-2xl border border-slate-700 space-y-1">
                  <p className="text-white font-semibold">CloudMortgage.io</p>
                  <p className="text-sm text-slate-400">
                    Cost per Loan: $0.53–$0.59 per case
                  </p>
                  <p className="text-sm text-slate-400">
                    Time to Result: 6–12 hours → fully automated
                  </p>
                  <p className="text-sm text-slate-400">
                    Scalability: Infinite
                  </p>
                  <p className="text-sm text-slate-400">
                    Accuracy/Auditability: High (consistent, traceable,
                    auditable)
                  </p>
                </div>
              </div>
              <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 space-y-3">
                <h4 className="text-xl font-semibold text-white">
                  CloudMortgage.io Pricing
                </h4>
                <div className="space-y-3 text-slate-300">
                  <div className="p-4 bg-slate-800/50 rounded-2xl border border-slate-700">
                    <p className="font-semibold text-white">
                      Starter – $39.99/month
                    </p>
                    <p className="text-sm text-slate-400">
                      50 cases per month • Cost per case ≈ $0.79
                    </p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-2xl border border-slate-700">
                    <p className="font-semibold text-white">
                      Pro – $79.99/month
                    </p>
                    <p className="text-sm text-slate-400">
                      150 cases per month • Cost per case ≈ $0.53
                    </p>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-2xl border border-slate-700">
                    <p className="font-semibold text-white">Free Tier</p>
                    <p className="text-sm text-slate-400">
                      10 cases → cost = $0
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-4">
            <button
              onClick={onBack}
              className="px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
      <PageFooter onReferralClick={onReferralClick} />
    </div>
  );
};

/**
 * Branded Login Page for Organization Portals
 * A simplified, branded landing page for org subdomains (e.g., loanfactory.cloudmortgage.io)
 */
const BrandedLoginPage = ({ branding, orgSlug, onLoginSuccess, onLogout }) => {
  const [currentView, setCurrentView] = useState("landing"); // 'landing', 'auth', 'dashboard'
  const [userEmail, setUserEmail] = useState("");
  const SESSION_KEY = "cm_user_session";

  // Check for existing session on mount
  useEffect(() => {
    const storedSession = localStorage.getItem(SESSION_KEY);
    if (storedSession) {
      try {
        const parsed = JSON.parse(storedSession);
        const storedRole = parsed?.role ? String(parsed.role).toLowerCase() : "";
        const storedEmail = parsed?.email || "";

        if (storedRole === "borrower") {
          window.location.href = "/borrower";
          return;
        }

        if (storedRole === "lender") {
          // Verify tokens exist before showing dashboard
          const tokens = readTokens();
          if (tokens?.accessToken) {
            setUserEmail(storedEmail);
            setCurrentView("dashboard");
          } else {
            // Session exists but tokens are missing - redirect to auth
            console.warn("[BrandedLoginPage] Session found but tokens missing - redirecting to auth");
            localStorage.removeItem(SESSION_KEY);
            setCurrentView("auth");
          }
        }
      } catch {
        localStorage.removeItem(SESSION_KEY);
      }
    }
  }, []);

  const handleLoginSuccess = async (authPayload) => {
    const email = typeof authPayload === "string" ? authPayload : authPayload?.email;
    const roleRaw = typeof authPayload === "object" ? authPayload?.role : undefined;
    const role = roleRaw ? String(roleRaw).toLowerCase() : undefined;
    const session = JSON.stringify({ email: email || "", role: role || "" });

    if (role === "borrower") {
      localStorage.setItem(SESSION_KEY, session);
      window.location.href = "/borrower";
      return;
    }

    if (role === "lender" || role === "realtor") {
      setUserEmail(email || "");
      setCurrentView("dashboard");
      localStorage.setItem(SESSION_KEY, session);

      // Pre-fetch subscription - don't trigger logout on failure
      fetchUserSubscription({ skipGlobalAuthHandler: true }).catch((error) => {
        console.warn("[BrandedLogin] Subscription pre-fetch failed (non-critical):", error.message);
      });
    } else {
      console.warn("[BrandedLoginPage] Invalid role, redirecting to auth. Role was:", roleRaw);
      localStorage.removeItem(SESSION_KEY);
      setCurrentView("auth");
    }
  };

  const handleLogout = () => {
    setUserEmail("");
    setCurrentView("landing");
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/";
  };

  // Show dashboard if logged in
  if (currentView === "dashboard") {
    return (
      <div className="relative">
        <Dashboard userEmail={userEmail} onLogout={handleLogout} />
      </div>
    );
  }

  // Show auth screen if in auth mode
  if (currentView === "auth") {
    return (
      <AuthScreen
        onLogin={handleLoginSuccess}
        onBack={() => setCurrentView("landing")}
      />
    );
  }

  // Get the display name - prefer org's company name, fall back to formatted slug
  const getDisplayName = () => {
    // If org has a custom company name that's not the default, use it
    if (branding.companyName && branding.companyName !== 'CloudMortgage.io') {
      return branding.companyName;
    }
    // Otherwise, format the slug (from branding data or URL)
    const slug = branding.slug || orgSlug;
    if (slug) {
      // Split by dashes/underscores, or try to split camelCase/common patterns
      let formatted = slug
        // Insert space before capital letters (camelCase)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        // Replace dashes and underscores with spaces
        .replace(/[-_]/g, ' ')
        // Capitalize first letter of each word
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      return formatted;
    }
    return 'Your Portal';
  };

  // Branded welcome page
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-4">
      <div className="text-center max-w-2xl mx-auto">
        {/* Welcome Message */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6">
          <span className="text-white">Welcome, </span>
          <span style={{ color: branding.primaryColor || '#4A9FD4' }}>
            {getDisplayName()} Team
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-xl md:text-2xl text-slate-400 mb-12">
          Access your A.I powered lending platform
        </p>

        {/* Sign In Button */}
        <button
          onClick={() => setCurrentView("auth")}
          className="px-10 py-5 text-xl font-semibold text-white rounded-xl transition-all duration-300 hover:opacity-90 hover:scale-105"
          style={{ backgroundColor: branding.primaryColor || '#4A9FD4' }}
        >
          Sign In to Continue
        </button>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const { branding, isOrgPortal, isLoading: isBrandingLoading, orgSlug } = useBranding();
  const [currentView, setCurrentView] = useState("landing"); // 'landing', 'auth', 'dashboard'
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUseCaseOpen, setIsUseCaseOpen] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const SESSION_KEY = "cm_user_session";
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isDetailedReportOpen, setIsDetailedReportOpen] = useState(false);
  const [isVirtualStagingOpen, setIsVirtualStagingOpen] = useState(false);
  const [activeTeam, setActiveTeam] = useState("mortgage");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-show login when ?login=true is in URL (from Flow landing page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('login') === 'true') {
      setCurrentView('auth');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  const handleLoginSuccess = async (authPayload) => {
    const email =
      typeof authPayload === "string" ? authPayload : authPayload?.email;
    const roleRaw =
      typeof authPayload === "object" ? authPayload?.role : undefined;
    const role = roleRaw ? String(roleRaw).toLowerCase() : undefined;
    const session = JSON.stringify({ email: email || "", role: role || "" });

    // Borrowers always to borrower portal
    if (role === "borrower") {
      localStorage.setItem(SESSION_KEY, session);
      window.location.href = "/borrower";
      return;
    }

    // Lender and Realtor roles land on dashboard; otherwise reset to auth
    if (role === "lender" || role === "realtor") {
      setUserEmail(email || "");
      setCurrentView("dashboard");
      localStorage.setItem(SESSION_KEY, session);

      // Fetch user subscription details - but don't logout on failure
      // The Dashboard will fetch this again and handle errors appropriately
      // This is just a pre-fetch optimization, not critical
      // skipGlobalAuthHandler prevents logout during initial login
      fetchUserSubscription({ skipGlobalAuthHandler: true }).catch((error) => {
        // Silently log - don't trigger logout for subscription fetch failures during login
        console.warn("[Login] Subscription pre-fetch failed (non-critical):", error.message);
      });
    } else {
      console.warn(
        "[App.js] Invalid role, redirecting to auth. Role was:",
        roleRaw,
      );
      localStorage.removeItem(SESSION_KEY);
      setCurrentView("auth");
    }
  };

  const handleLogout = (silent) => {
    // Clear all session data
    setUserEmail("");
    setCurrentView("landing");

    // Clear all storage
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    localStorage.clear();
    sessionStorage.clear();

    // Force redirect to landing page to ensure clean state
    window.location.href = "/";
  };

  // Register global authentication error handler
  useEffect(() => {
    // Set up global handler that will be called when authentication fails anywhere in the app
    setGlobalAuthErrorHandler((error) => {
      console.error("Global authentication error detected:", error);
      handleLogout(true);
    });

    // Clean up on unmount
    return () => {
      clearGlobalAuthErrorHandler();
    };
  }, []); // Empty deps - only run once on mount

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasCheckoutFlag =
      searchParams.get("success") || searchParams.get("canceled");
    const hasViewParam = searchParams.get("view");
    const path = window.location.pathname;

    // Handle /notes path for deep linking - redirect to auth if not logged in
    // so user can login and then be taken to the note
    if (path === "/notes") {
      const storedSession = localStorage.getItem(SESSION_KEY);
      if (!storedSession) {
        // No session - redirect to auth for login
        setCurrentView("auth");
        return;
      }
      // Session exists - normal flow below will validate and show dashboard
      // Dashboard will detect /notes path and show notes view
    }

    // Handle view=marketing from social OAuth callback
    if (hasViewParam === "marketing") {
      const storedSession = localStorage.getItem(SESSION_KEY);
      if (storedSession) {
        try {
          const parsed = JSON.parse(storedSession);
          const storedRole = parsed?.role
            ? String(parsed.role).toLowerCase()
            : "";
          const storedEmail = parsed?.email || "";

          if (storedRole === "lender") {
            // Verify tokens exist before showing dashboard
            const tokens = readTokens();
            if (tokens?.accessToken) {
              setUserEmail(storedEmail);
              setCurrentView("dashboard");
              // The Dashboard component will handle the view=marketing param
              return;
            } else {
              // Session exists but tokens are missing - redirect to auth
              console.warn("[LandingPage] view=marketing: Session found but tokens missing - redirecting to auth");
              localStorage.removeItem(SESSION_KEY);
              setCurrentView("auth");
              return;
            }
          }
        } catch {
          // Fall through to normal flow
        }
      }
    }

    // CRITICAL: If returning from checkout, restore tokens FIRST and force dashboard view
    if (hasCheckoutFlag) {
      // Restore tokens synchronously before any other logic
      try {
        const currentTokens = readTokens();
        const backupTokens = window.localStorage.getItem(
          "cm_auth_tokens_backup",
        );

        if (!currentTokens && backupTokens) {
          persistTokens(JSON.parse(backupTokens), false);
          window.localStorage.removeItem("cm_auth_tokens_backup");
        }
      } catch (error) {
        console.error("[Checkout Return] Token restoration failed:", error);
      }

      // Get session info
      const storedSession = localStorage.getItem(SESSION_KEY);

      // For checkout returns, verify tokens exist before showing dashboard
      const tokensAfterRestore = readTokens();
      if (!tokensAfterRestore?.accessToken) {
        console.warn("[LandingPage] Checkout return: No valid tokens after restoration - redirecting to auth");
        localStorage.removeItem(SESSION_KEY);
        setCurrentView("auth");
        return;
      }

      // Tokens exist, proceed to dashboard
      try {
        const parsed = JSON.parse(storedSession);
        const storedEmail = parsed?.email || "";
        if (storedEmail) {
          setUserEmail(storedEmail);
        }
      } catch {
        // If session is a plain email string (legacy)
        if (storedSession && storedSession.includes("@")) {
          setUserEmail(storedSession);
        }
      }

      setCurrentView("dashboard");
      return;
    }

    // Normal flow (not from checkout)
    const storedSession = localStorage.getItem(SESSION_KEY);
    if (!storedSession) {
      return;
    }

    try {
      const parsed = JSON.parse(storedSession);
      const storedRole = parsed?.role ? String(parsed.role).toLowerCase() : "";
      const storedEmail = parsed?.email || "";

      if (storedRole === "borrower") {
        window.location.href = "/borrower";
        return;
      }

      if (storedRole === "lender") {
        // Verify tokens exist before showing dashboard
        const tokens = readTokens();
        if (tokens?.accessToken) {
          setUserEmail(storedEmail);
          setCurrentView("dashboard");
          return;
        } else {
          // Session exists but tokens are missing (e.g., browser was closed)
          // Clear stale session and redirect to auth
          console.warn("[LandingPage] Normal flow: Session found but tokens missing - redirecting to auth");
          localStorage.removeItem(SESSION_KEY);
          setCurrentView("auth");
          return;
        }
      }
    } catch {
      // legacy string sessions fall through to auth
    }

    // Unknown or legacy session: clear and stay on landing/auth
    localStorage.removeItem(SESSION_KEY);
  }, []);

  useEffect(() => {
    const pendingCaseId = localStorage.getItem("cm_pending_case_id");
    if (!pendingCaseId) return;
    const storedSession = localStorage.getItem(SESSION_KEY);
    if (!storedSession) {
      setCurrentView("auth");
    }
  }, []);

  // Show loading state while branding is being fetched for org portals
  if (isBrandingLoading && isOrgPortal) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  // For organization portals, use the simplified branded login page
  if (isOrgPortal) {
    return (
      <BrandedLoginPage
        branding={branding}
        orgSlug={orgSlug}
        onLoginSuccess={handleLoginSuccess}
        onLogout={handleLogout}
      />
    );
  }

  if (currentView === "usecase-lender" || currentView === "usecase-borrower") {
    const isLender = currentView === "usecase-lender";
    const title = isLender ? "Use Case: Lenders" : "Use Case: Borrowers";
    const subtitle = isLender
      ? "Process loans faster. Deliver clarity and speed. Grow without adding headcount."
      : "A smoother, more transparent mortgage experience—powered by a lender who finally has time for you.";
    const body = isLender
      ? `Modern underwriting is a race against time. Borrowers expect answers in hours, not days — yet underwriting teams still sift through tax returns, bank statements, K-1s, mortgages, liabilities, and assets one line at a time. Every delay slows the pipeline, strains staff, and limits the number of loans you can close each month.

CloudMortgage.io changes that.

Our platform automates the heavy lifting that traditionally consumes 3–6 hours per loan file. The system extracts all documents, reconciles income, assets, liabilities, property details, and generates a complete, source-linked underwriting summary — precise, transparent, and audit-ready. Your underwriter stays fully in control, but no longer has to build the analysis from scratch.

What used to take hours becomes a review step that takes minutes.

Underwriters get clean workups instead of messy files. Managers eliminate backlog without hiring more staff. Loan officers get faster turn-times and stronger pull-through rates. Realtors trust you because you consistently provide clarity and speed — not excuses or delays.

With CloudMortgage.io, your team frees up time to serve more borrowers, deepen relationships, and scale sustainably.
This is underwriting reinvented — enterprise-grade, compliant, and built for lenders who demand excellence.`
      : `A smoother, more transparent mortgage experience — powered by a lender who finally has time for you.

Borrowers don’t see the long nights and manual work behind every mortgage. They only feel the waiting.
Waiting for questions.
Waiting for conditions.
Waiting for the underwriter to finish reviewing stacks of documents.

CloudMortgage.io transforms that experience.

When lenders use our platform, your loan moves faster because the underwriting team no longer spends days piecing together financials line by line. The system organizes your documents, calculates your income and assets, and prepares a clean summary for review — giving your lender the clarity they need to serve you quickly and confidently.

That means fewer back-and-forth emails.
Fewer confusing requests.
Faster decisions.
More trust in the process.

Instead of being buried in paperwork, your lender actually has time to guide you — explaining next steps, answering questions, and moving your loan toward approval without unnecessary delays.

The result is an experience that feels effortless: fast, transparent, and human.
Borrowers choose lenders who guide them with clarity and speed. CloudMortgage.io helps your lender deliver both — effortlessly.`;
    return (
      <UseCasePage
        title={title}
        subtitle={subtitle}
        body={body}
        onBack={() => setCurrentView("landing")}
        onSelectUseCase={(target) =>
          setCurrentView(
            target === "lender" ? "usecase-lender" : "usecase-borrower",
          )
        }
        onReferralClick={() => setCurrentView("referral-program")}
      />
    );
  }

  if (currentView === "referral-program") {
    return <ReferralProgramPage onBack={() => setCurrentView("landing")} />;
  }

  if (currentView === "auth") {
    return (
      <AuthScreen
        onLogin={handleLoginSuccess}
        onBack={() => setCurrentView("landing")}
      />
    );
  }

  if (currentView === "dashboard") {
    return (
      <div className="relative">
        <Dashboard userEmail={userEmail} onLogout={handleLogout} />
      </div>
    );
  }

  return (
    <div className="min-h-screen font-sans text-slate-100 bg-slate-950 scroll-smooth">
      <ParticleCanvas />
      <LandingHeader
        isScrolled={isScrolled}
        scrollToSection={scrollToSection}
        onLoginClick={() => setCurrentView("auth")}
        onReferralClick={() => setCurrentView("referral-program")}
        onBlogClick={() => {
          window.location.href = "/blog";
        }}
        onSelectUseCase={(target) =>
          setCurrentView(
            target === "lender" ? "usecase-lender" : "usecase-borrower",
          )
        }
        isUseCaseOpen={isUseCaseOpen}
        setIsUseCaseOpen={setIsUseCaseOpen}
        isMobileMenuOpen={isMobileMenuOpen}
        toggleMobileMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      <HeroSection
        scrollToSection={scrollToSection}
        onSummaryClick={() => setIsSummaryModalOpen(true)}
        onDetailedReportClick={() => setIsDetailedReportOpen(true)}
        onVirtualStagingClick={() => setIsVirtualStagingOpen(true)}
      />
      <ProblemSection activeTeam={activeTeam} setActiveTeam={setActiveTeam} />
      {activeTeam === "realty" && <RealtorFeaturesSlider />}
      <ProductShowcaseSection onLoginClick={() => setCurrentView("auth")} activeTeam={activeTeam} />
      {activeTeam === "mortgage" && <PricingCalculator />}
      <PricingSection onLoginClick={() => setCurrentView("auth")} activeTeam={activeTeam} />
      <FAQSection activeTeam={activeTeam} />
      <PageFooter onReferralClick={() => setCurrentView("referral-program")} />
      <SampleSummaryModal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
      />
      <DetailedUnderwritingReport
        isOpen={isDetailedReportOpen}
        onClose={() => setIsDetailedReportOpen(false)}
      />
      <VirtualStagingModal
        isOpen={isVirtualStagingOpen}
        onClose={() => setIsVirtualStagingOpen(false)}
      />
    </div>
  );
};

export default LandingPage;
